import { useEffect, useMemo } from 'react';
import { useReportBuilderStore } from '../../../stores/reportBuilderStore';
import { Database, Check, LinkSimple, CircleNotch } from '@phosphor-icons/react';

const MODULE_LABELS: Record<string, string> = {
  core: 'Core', review_cloud: 'Review Cloud', goals: 'Goals', surveys: 'Surveys', pip: 'PIP',
};

export default function ModelSelectionStep() {
  const {
    models, modelsLoading, selectedModelKeys, modelLoading,
    fetchModels, toggleModel, getJoinableModelKeys,
  } = useReportBuilderStore();

  useEffect(() => {
    if (models.length === 0) fetchModels();
  }, [models.length, fetchModels]);

  const joinableKeys = getJoinableModelKeys();
  const hasPrimary = selectedModelKeys.length > 0;

  const groupedModels = useMemo(() => {
    const groups: Record<string, typeof models> = {};
    for (const m of models) {
      const mod = m.module || 'other';
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(m);
    }
    return groups;
  }, [models]);

  if (modelsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Select Data Models</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose a primary data model, then optionally join additional models for cross-table analysis.
        </p>
      </div>

      {Object.entries(groupedModels).map(([mod, items]) => (
        <div key={mod}>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {MODULE_LABELS[mod] || mod}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((m) => {
              const isSelected = selectedModelKeys.includes(m.key);
              const isPrimary = selectedModelKeys[0] === m.key;
              const isJoinable = !hasPrimary || isSelected || joinableKeys.has(m.key);
              const disabled = !isJoinable || modelLoading;

              return (
                <button
                  key={m.key}
                  onClick={() => toggleModel(m.key)}
                  disabled={disabled}
                  className={`relative rounded-lg border-2 p-4 text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : disabled
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-50'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Database size={18} className={isSelected ? 'text-blue-600' : 'text-gray-400'} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {m.name}
                      </span>
                    </div>
                    {isSelected && (
                      <div className={`flex h-5 w-5 items-center justify-center rounded-full ${isPrimary ? 'bg-primary' : 'bg-indigo-500'} text-white`}>
                        {isPrimary ? <Check size={12} weight="bold" /> : <LinkSimple size={12} />}
                      </div>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500 line-clamp-2">{m.description}</p>
                  {isSelected && !isPrimary && (
                    <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600">
                      <LinkSimple size={10} /> Joined
                    </span>
                  )}
                  {isPrimary && (
                    <span className="mt-2 inline-flex text-[10px] font-medium text-blue-600">Primary Model</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
